/**
 * Pure moderation decisions for `apps/app`'s messaging surface.
 *
 * `canDeleteMessage` never derives a role. It reads `canModerate`, the
 * server-computed boolean off `/chat/get`. That boolean already agrees with
 * `postMessageDeleteHandler`'s own `isModerator` check in every reachable
 * case (private chats never promote, standalone chats only ever hold
 * "member" or "admin", and pool chats' "commissioner" and "admin" both
 * satisfy `role !== "member"`), so this file is a mirror of the server gate,
 * not a second implementation of it. If that ever stops being true the
 * server still refuses with `NOT_MESSAGE_OWNER`.
 *
 * `isFailedSdkResult` and `sdkErrorCode` exist because generated response
 * types declare both `error` and `data` optional, and this app compiles
 * under `exactOptionalPropertyTypes`, so `undefined` must be normalized to
 * "absent" rather than treated as a value. ISSUE-648 made an empty non-2xx
 * a real error envelope in `packages/codegen`. This helper still treats
 * `data === null` as failure.
 */

export function canDeleteMessage(payload: {
  senderId: string;
  callerId: string;
  canModerate: boolean;
}): boolean {
  return payload.senderId === payload.callerId || payload.canModerate;
}

export function isBlockedSender(payload: {
  senderId: string;
  blockedAccountIds: ReadonlySet<string>;
}): boolean {
  return payload.blockedAccountIds.has(payload.senderId);
}

/** True when the reported message is the viewer's own. The server refuses a self block with VALIDATION_ERROR / cannotBlockSelf, so a caller must never offer or send one. */
export function isViewerSender(payload: {
  senderId: string;
  viewerUserId: string;
}): boolean {
  return (
    payload.viewerUserId !== "" && payload.senderId === payload.viewerUserId
  );
}

export type typePendingRequestItem = { chatId: string; fromUserId: string };

export function findPendingRequestForSender(payload: {
  items: readonly typePendingRequestItem[];
  senderAccountId: string;
}): { chatId: string } | null {
  const match = payload.items.find(
    (item) => item.fromUserId === payload.senderAccountId,
  );
  return match ? { chatId: match.chatId } : null;
}

export function isFailedSdkResult(result: {
  error?: { code: string } | null | undefined;
  data?: unknown;
}): boolean {
  return (result.error ?? null) !== null || (result.data ?? null) === null;
}

export function sdkErrorCode(result: {
  error?: { code: string } | null | undefined;
}): string | null {
  return result.error?.code ?? null;
}
